import React from 'react'

import { Box, Button, CornerIndicator, Text } from '@factorialco/deprecated-design-system'
import { Field } from 'factorial-form'
import {
  CanBulkCreateContractsTaxonomiesDocument,
  ContractTaxonomyFragment,
} from 'generated/resources/graphql'

import DurationInput from 'modules/core/components/DurationInput'
import type { Params } from 'modules/core/components/Inputs/AutoSaveInput'
import Select from 'modules/core/components/Inputs/Select'
import TextInput from 'modules/core/components/Inputs/Text'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useQuery } from 'modules/core/hooks/useQuery'
import asyncAction from 'modules/core/lib/asyncAction'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import Compensation from 'modules/contracts/store/models/Compensation'
import type Employee from 'modules/employees/store/models/Employee'
import AmountInputIndicator from 'modules/payroll_bundle/components/Handoffs/Supplements/Components/AmountInputIndicator'
import TaxonomyDropdown from 'modules/payroll_bundle/components/Handoffs/Supplements/Components/TaxonomyDropdown'
import {
  checkAndProcessForm,
  createOrUpdateSupplementForm,
  deleteSupplementAndUpdateForm,
  fetchSupplementsWithParams,
} from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/List/Line'
import CalculatorPopover from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/List/Line/Form/CalculatorPopover'
import ControlInput from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/List/Line/Form/ControlInput'
import {
  CURRENCY_SUB_UNIT,
  labelCurrency,
} from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/List/Line/Form/index'
import SupplementForm from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/SupplementForm'
import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import useFetchSupplementInsights from 'modules/payroll_bundle/hooks/useFetchSupplementInsights'
import { PayrollStatus } from 'modules/payroll_bundle/lib/enums'
import { useFrequency } from 'modules/payroll_bundle/lib/frequencyContext'
import { getPercentage } from 'modules/payroll_bundle/lib/percentage'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { roundToDecimals } from 'modules/payroll_bundle/lib/roundToDecimals'
import Supplement, {
  decimalHoursToFullMinutes,
  minutesToDecimalHours,
} from 'modules/payroll_bundle/store/models/Supplement'

interface Props {
  effectiveOn: moment.Moment
  employee: Employee
  hover: boolean
  policyPeriodId: string
  store: SupplementsFormStore
  taxonomies: ContractTaxonomyFragment[]
  toggleTaxonomyCreationModal: (value: boolean) => void
  handleSupplementCreationModalToggle: (newState: boolean) => void
}

const SupplementCreateContent = ({
  effectiveOn,
  employee,
  hover,
  policyPeriodId,
  store,
  taxonomies,
  handleSupplementCreationModalToggle,
  toggleTaxonomyCreationModal,
}: Props) => {
  const defaultFormParams = {
    amount_in_cents: 0,
    unit: 'money',
    effective_on: dateToString(effectiveOn, 'backend'),
    employee_id: employee.attributes.id,
    contracts_taxonomy_id: null,
  }

  const newForm = useLazyRef(() => new SupplementForm(defaultFormParams)).current

  const [isCreating, setIsCreating] = React.useState(false)
  const [currentUnit, setCurrentUnit] = React.useState(newForm.get('unit').value)

  const {
    access: { id: currentUserId },
  } = useCurrentSession()

  const { currentPolicyPeriods, periodIndex, currentPeriod } = usePolicyPeriods()

  const { fetchInsightsData } = useFetchSupplementInsights({
    triggerFetch: false,
  })

  const policyPeriodStatus = currentPeriod?.attributes.status

  const statusInCalculationOrPaid =
    policyPeriodStatus === PayrollStatus.PAYMENT_PROCESSING ||
    policyPeriodStatus === PayrollStatus.PAID ||
    policyPeriodStatus === PayrollStatus.PAYROLL_CALCULATION

  const { date, frequency } = useFrequency()

  const { data } = useQuery(CanBulkCreateContractsTaxonomiesDocument)

  const canBulkCreateContractsTaxonomies =
    !!data?.contracts._policies.contractBulkCreateAdditionalCompensationType.can

  interface ErrorResult {
    amount?: string
    taxonomy?: string
  }

  const [percentage, setPercentage] = React.useState(0)
  const [isChangeFromAmount, setIsChangeFromAmount] = React.useState(false)
  const [initialAmount, setInitialAmount] = React.useState(0)

  const [error, setError] = React.useState<ErrorResult>({})

  const zeroUpToAmount = 0

  let periodStart = effectiveOn
  const policyPeriod = currentPolicyPeriods[periodIndex]
  periodStart = stringToDate(policyPeriod?.attributes.starts_on, 'backend')

  const onAddForm = (formProps?: Partial<SupplementForm>) => {
    store.addForm(employee, periodStart, formProps)
  }

  const fetchSupplements = () => {
    return fetchSupplementsWithParams(policyPeriodId, date, frequency)
  }

  const deleteSupplementByContract = async () => {
    await deleteSupplementAndUpdateForm(newForm, fetchSupplements, store)
  }

  const createOrUpdateForm = async (form: SupplementForm): Promise<SupplementForm> => {
    await createOrUpdateSupplementForm(form, setIsCreating, fetchInsightsData, isCreating)
    return form
  }

  interface FormSubmissionResult {
    success?: boolean
    taxonomyError?: string
    amountError?: string
  }

  const onSubmitClick: () => Promise<FormSubmissionResult> = async () => {
    newForm.get('created_by_access_id').set(currentUserId)

    await checkAndProcessForm(newForm, policyPeriodId, deleteSupplementByContract)

    const supplementObject = newForm.supplementData
    const taxonomyId = newForm.contractTaxonomyId

    const taxonomyError =
      taxonomyId !== null
        ? undefined
        : i18n.t('payroll.period.variable_compensations.validation.missing_taxonomy')
    const amountError =
      supplementObject.amount_in_cents != 0
        ? undefined
        : i18n.t('payroll.period.variable_compensations.validation.incorrect_amount')

    setError({
      taxonomy: taxonomyError,
      amount: amountError,
    })

    if (
      supplementObject.payroll_policy_period_id !== null &&
      supplementObject.amount_in_cents != 0 &&
      taxonomyId !== null
    ) {
      await asyncAction(() => createOrUpdateForm(newForm), {
        successFlash: false,
      })

      onAddForm(newForm)

      return Promise.resolve({ success: true })
    } else {
      return Promise.resolve({ taxonomyError, amountError })
    }
  }

  const resetForm = () => {
    newForm.setValues(defaultFormParams)
  }

  const onAmountChange = (field: Field) => {
    if (field.value > 0) {
      const totalUpToAmount = newForm.compensation?.attributes.amount / CURRENCY_SUB_UNIT

      const percentageValue = getPercentage(field.value, totalUpToAmount)
      setPercentage(roundToDecimals(percentageValue))
      setIsChangeFromAmount(true)
      setInitialAmount(field.value)
    }
  }

  function handleTaxonomyCreationModalToggle(newState: boolean) {
    toggleTaxonomyCreationModal(newState)
    resetForm()
  }

  return (
    <Box
      gap="s16"
      paddingX="s24"
      paddingY="s12"
      width="full"
      aria-label="supplement-create-content"
    >
      <ControlInput isSelect>
        {(params: Params) => (
          <Select
            {...params}
            field={newForm.get('unit')}
            readonly={false}
            label={i18n.t('handoffs.supplements.labels.format')}
            options={Object.keys(Supplement.units).map((value) => ({
              value,
              label: labelCurrency(value, newForm),
            }))}
            onChange={(field) => {
              setCurrentUnit(field.value)
            }}
          />
        )}
      </ControlInput>

      <ControlInput>
        {(params: Params) =>
          currentUnit === Supplement.units.time ? (
            <DurationInput
              {...params}
              label={i18n.t('handoffs.supplements.labels.time')}
              readonly={false}
              minutes={decimalHoursToFullMinutes(newForm.get('amount_in_cents').value)}
              onChange={(value) =>
                newForm.get('amount_in_cents').set(String(minutesToDecimalHours(value)))
              }
            />
          ) : (
            <TextInput
              {...params}
              readonly={false}
              field={newForm.get('amount_in_cents')}
              label={i18n.t('handoffs.supplements.labels.amount')}
              isClickableIcon
              mandatory
              onChange={onAmountChange}
              placeholder={
                newForm.compensationType === Compensation.types.up_to &&
                statusInCalculationOrPaid &&
                newForm.get('amount_in_cents').value.length === 0
                  ? zeroUpToAmount
                  : newForm.get('description').value
              }
              error={error?.amount}
              indicator={
                <Box flexDirection="row" alignItems="center" gap="s8">
                  <AmountInputIndicator
                    form={newForm}
                    isCalculationOrPaid={statusInCalculationOrPaid}
                    percentageNumber={percentage}
                  />

                  <CalculatorPopover
                    form={newForm}
                    percentage={percentage}
                    setPercentage={setPercentage}
                    initialAmount={initialAmount}
                    setInitialAmount={setInitialAmount}
                    isChangeFromAmount={isChangeFromAmount}
                    setIsChangeFromAmount={setIsChangeFromAmount}
                    hover={hover}
                  />
                </Box>
              }
            />
          )
        }
      </ControlInput>

      <Box flexDirection="column" alignItems="flexStart" gap="s8" width="full">
        <Text.Normal size="200" weight="medium" color="grey1000">
          {i18n.t('handoffs.supplements.labels.type')}
        </Text.Normal>
        <Box width="full" gap="s6" position="relative">
          <TaxonomyDropdown
            isReadOnly={false}
            form={newForm}
            taxonomies={taxonomies}
            hasError={!!error?.taxonomy}
            canBulkCreateContractsTaxonomies={canBulkCreateContractsTaxonomies}
            onSubmit={async () => {}}
            compensationsTypeSize="expansive"
            toggleTaxonomyCreationModal={handleTaxonomyCreationModalToggle}
          />

          {error?.taxonomy && (
            <>
              <CornerIndicator
                attachmentPosition={'bottom center'}
                errors
                missing
                missingLabel={i18n.t('common.missing_field')}
              />
              <Box alignItems="flexStart">
                <Text size="200" color="radicalRed1000" capitalize>
                  {error?.taxonomy}
                </Text>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Box width="full" alignSelf="flexEnd">
        <Button
          onClick={async () => {
            const { taxonomyError, amountError } = await onSubmitClick()

            if (!taxonomyError && !amountError) {
              handleSupplementCreationModalToggle(false)
              resetForm()
            }
          }}
        >
          {i18n.t('common.save')}
        </Button>
      </Box>
    </Box>
  )
}

export default SupplementCreateContent
